import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Info, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import i18n from '@/utils/i18n';

import FeedbackBanner from '../../components/FeedbackBanner';
import SegmentedControl from './editContainerInfoNew/SegmentedControl';
import GroupEditorPanel from './editContainerTypeGroupsBeta/GroupEditorPanel';
import MobileContainerTypeGroups from './editContainerTypeGroupsBeta/MobileContainerTypeGroups';
import { useContainerTypeGroupsAdmin } from './editContainerTypeGroupsBeta/useContainerTypeGroupsAdmin';
import {
  categoryLabel,
  filterGroups,
  isDeleteGuarded,
  isDraftDirty,
  memberGlyph,
  type CategoryFilter,
  type ContainerTypeGroup,
  type GroupCategory,
  type GroupDraft,
} from './editContainerTypeGroupsBeta/groupHelpers';

const BRAND_GREEN = 'var(--brand-green, oklch(0.685 0.163 145))';
const FEEDBACK_PATH = 'edit-container-type-groups';

type EditorSession = { mode: 'create' | 'edit'; groupId: number | null };

const EMPTY_DRAFT: GroupDraft = { name: '', category: null };

/**
 * Redesigned "Container type groups" screen (handoff v2): table on desktop /
 * cards on mobile, a create+edit sheet replacing the two legacy modals, guarded
 * deletes with surfaced reasons and explicit unsaved state. Served entirely by
 * the colugo GraphQL admin API. Shipped alongside the legacy screen under the
 * `-beta` route; the legacy screen links here via a "new design" banner.
 */
export default function EditContainerTypeGroupsBeta() {
  const isMobile = useIsMobile();
  const admin = useContainerTypeGroupsAdmin();

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');

  const [session, setSession] = useState<EditorSession | null>(null);
  const [draft, setDraft] = useState<GroupDraft>(EMPTY_DRAFT);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const filtered = useMemo(
    () => filterGroups(admin.groups, search, categoryFilter),
    [admin.groups, search, categoryFilter],
  );

  const selectedGroup: ContainerTypeGroup | null =
    session?.mode === 'edit' && session.groupId != null
      ? (admin.groups.find((g) => g.id === session.groupId) ?? null)
      : null;

  const editorOpen = session != null;
  const isCreate = session?.mode === 'create';
  const dirty = isDraftDirty(draft, isCreate ? null : selectedGroup);
  const canSave = dirty && draft.name.trim() !== '';

  const clearConfirmTimer = () => {
    if (confirmTimer.current) clearTimeout(confirmTimer.current);
    confirmTimer.current = null;
  };

  const openGroup = (group: ContainerTypeGroup) => {
    clearConfirmTimer();
    setConfirmDelete(false);
    setSession({ mode: 'edit', groupId: group.id });
    setDraft({
      name: group.name ?? '',
      category: (group.category as GroupCategory) ?? null,
    });
  };

  const openNew = () => {
    clearConfirmTimer();
    setConfirmDelete(false);
    setSession({ mode: 'create', groupId: null });
    setDraft(EMPTY_DRAFT);
  };

  const closeEditor = () => {
    clearConfirmTimer();
    setConfirmDelete(false);
    setSession(null);
  };

  const submitEditor = async () => {
    if (!canSave) return;
    if (isCreate) {
      const ok = await admin.createGroup(draft.name, draft.category);
      if (ok) closeEditor();
      return;
    }
    if (selectedGroup) {
      await admin.saveGroup(selectedGroup.id, {
        name: draft.name,
        category: draft.category,
      });
      // On success the list refetches to the saved values; keep the sheet open
      // (now clean), matching the handoff.
    }
  };

  const handleSheetDelete = async () => {
    if (!selectedGroup || isDeleteGuarded(selectedGroup)) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      clearConfirmTimer();
      confirmTimer.current = setTimeout(() => setConfirmDelete(false), 3000);
      return;
    }
    clearConfirmTimer();
    const ok = await admin.deleteGroup(selectedGroup.id);
    if (ok) closeEditor();
  };

  const handleRowDelete = (group: ContainerTypeGroup) => {
    if (isDeleteGuarded(group)) return;
    admin.deleteGroup(group.id);
  };

  // Close the sheet if the edited group vanished after a refetch (edge case).
  useEffect(() => {
    if (
      session?.mode === 'edit' &&
      session.groupId != null &&
      selectedGroup == null &&
      !admin.loading
    ) {
      closeEditor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, selectedGroup, admin.loading]);

  const editorPanel = (
    <GroupEditorPanel
      open={editorOpen}
      variant={isMobile ? 'mobile' : 'desktop'}
      mode={session?.mode ?? 'edit'}
      group={selectedGroup}
      draft={draft}
      dirty={dirty}
      canSave={canSave}
      saving={admin.creating || admin.updating}
      confirmDelete={confirmDelete}
      deleting={admin.deleting}
      onNameChange={(name) => setDraft((d) => ({ ...d, name }))}
      onCategoryChange={(category) => setDraft((d) => ({ ...d, category }))}
      onClose={closeEditor}
      onSubmit={submitEditor}
      onDeleteClick={handleSheetDelete}
    />
  );

  const feedbackBanner = <FeedbackBanner path={FEEDBACK_PATH} />;

  if (admin.error) {
    return (
      <div className="tw:p-8 tw:text-sm tw:text-destructive">
        {admin.error.message}
      </div>
    );
  }

  if (admin.loading) {
    return (
      <div className="tw:p-8 tw:text-sm tw:text-muted-foreground">
        {i18n.t('Common:loading')}
      </div>
    );
  }

  if (isMobile) {
    return (
      <>
        <MobileContainerTypeGroups
          rows={filtered}
          totalCount={admin.groups.length}
          search={search}
          onSearchChange={setSearch}
          categoryFilter={categoryFilter}
          onCategoryFilterChange={setCategoryFilter}
          onOpen={openGroup}
          onNew={openNew}
          feedbackSlot={feedbackBanner}
        />
        {editorPanel}
      </>
    );
  }

  return (
    <div className="tw:flex tw:max-w-[1100px] tw:flex-col tw:gap-4.5 tw:p-6 tw:pb-16">
      {feedbackBanner}

      {/* 1. Header */}
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-4">
        <div className="tw:flex tw:flex-col tw:gap-1.5">
          <h1 className="tw:text-3xl tw:font-semibold tw:tracking-tight">
            {i18n.t('EditContainerTypeGroups:pageTitle')}
          </h1>
          <p className="tw:max-w-2xl tw:text-sm tw:leading-relaxed tw:text-muted-foreground">
            {i18n.t('EditContainerTypeGroups:pageSubtitle')}
          </p>
        </div>
        <Button className="tw:flex-none" onClick={openNew}>
          <Plus size={15} />
          {i18n.t('EditContainerTypeGroups:newGroup')}
        </Button>
      </div>

      {/* 2. Info callout */}
      <div className="tw:flex tw:items-start tw:gap-2.5 tw:rounded-md tw:border tw:border-border tw:bg-muted tw:px-3.5 tw:py-3">
        <Info
          size={16}
          className="tw:mt-0.5 tw:flex-none tw:text-muted-foreground"
        />
        <p className="tw:text-[13px] tw:leading-relaxed tw:text-muted-foreground">
          {i18n.t('EditContainerTypeGroups:deleteRule')}
        </p>
      </div>

      {/* 3. Toolbar */}
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2.5">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={i18n.t('EditContainerTypeGroups:searchPlaceholder')}
          className="tw:h-9 tw:w-[300px] tw:max-w-full tw:rounded-md tw:border tw:border-input tw:bg-background tw:px-3 tw:text-sm tw:text-foreground tw:shadow-xs tw:outline-none"
        />
        <SegmentedControl<CategoryFilter>
          options={[
            {
              value: 'all',
              label: i18n.t('EditContainerTypeGroups:filterAll'),
            },
            {
              value: 'Bowls',
              label: i18n.t('EditContainerTypeGroups:categoryBowls'),
            },
            {
              value: 'Cups',
              label: i18n.t('EditContainerTypeGroups:categoryCups'),
            },
            {
              value: 'none',
              label: i18n.t('EditContainerTypeGroups:noCategory'),
            },
          ]}
          value={categoryFilter}
          onChange={setCategoryFilter}
        />
        <div className="tw:flex-1" />
        <span className="tw:text-[13px] tw:text-muted-foreground">
          {i18n.t('EditContainerTypeGroups:countLabel', {
            shown: filtered.length,
            total: admin.groups.length,
          })}
        </span>
      </div>

      {/* 4. Table */}
      <div className="tw:overflow-hidden tw:rounded-lg tw:border tw:border-border tw:bg-background">
        {/* Header row */}
        <div className="tw:grid tw:grid-cols-[minmax(200px,1.1fr)_150px_minmax(240px,1.2fr)_140px_52px] tw:items-center tw:border-b tw:border-border tw:bg-muted tw:px-3.5 tw:text-[12.5px] tw:font-semibold tw:text-muted-foreground tw:[height:40px]">
          <span>{i18n.t('EditContainerTypeGroups:colGroup')}</span>
          <span>{i18n.t('EditContainerTypeGroups:category')}</span>
          <span>{i18n.t('EditContainerTypeGroups:containerTypes')}</span>
          <span>{i18n.t('EditContainerTypeGroups:linkedStores')}</span>
          <span />
        </div>

        {filtered.length === 0 ? (
          <div className="tw:border-t tw:border-border tw:px-3.5 tw:py-9 tw:text-center tw:text-[13.5px] tw:text-muted-foreground">
            {i18n.t('EditContainerTypeGroups:noGroupsMatch')}
          </div>
        ) : (
          filtered.map((group) => {
            const guarded = isDeleteGuarded(group);
            const selected =
              session?.mode === 'edit' && session.groupId === group.id;
            const Glyph = memberGlyph(
              (group.category as GroupCategory) ?? null,
            );
            const thumbs = group.container_types.slice(0, 5);
            const delTitle = guarded
              ? group.has_linked_stores
                ? i18n.t('EditContainerTypeGroups:cannotDeleteStores')
                : i18n.t('EditContainerTypeGroups:cannotDeleteTypes')
              : i18n.t('EditContainerTypeGroups:deleteGroup');
            return (
              <div
                key={group.id}
                role="button"
                tabIndex={0}
                onClick={() => openGroup(group)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openGroup(group);
                  }
                }}
                className={
                  'tw:-mt-px tw:grid tw:cursor-pointer tw:grid-cols-[minmax(200px,1.1fr)_150px_minmax(240px,1.2fr)_140px_52px] tw:items-center tw:border-t tw:border-border tw:px-3.5 tw:py-2.5 tw:outline-none ' +
                  (selected
                    ? 'tw:bg-muted'
                    : 'tw:bg-transparent tw:hover:bg-accent')
                }
              >
                {/* Group */}
                <div className="tw:flex tw:min-w-0 tw:flex-col tw:pr-3">
                  <span className="tw:truncate tw:text-sm tw:font-medium">
                    {group.name}
                  </span>
                  <span className="tw:font-mono tw:text-[11.5px] tw:text-muted-foreground">
                    #{group.id}
                  </span>
                </div>

                {/* Category */}
                <div>
                  {group.category ? (
                    <span className="tw:inline-flex tw:h-[22px] tw:items-center tw:rounded-md tw:border tw:border-border tw:bg-background tw:px-2.5 tw:text-xs tw:font-medium">
                      {categoryLabel(group.category as GroupCategory)}
                    </span>
                  ) : (
                    <span className="tw:text-[13px] tw:text-muted-foreground">
                      -
                    </span>
                  )}
                </div>

                {/* Container types */}
                <div className="tw:flex tw:items-center tw:gap-2.5">
                  <div className="tw:flex tw:gap-1.5">
                    {thumbs.map((member) => (
                      <span
                        key={member.id}
                        title={member.name}
                        className="tw:flex tw:size-7 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-lg tw:border tw:border-border tw:bg-muted tw:text-muted-foreground"
                      >
                        {member.image_url ? (
                          <img
                            src={member.image_url}
                            alt={member.name}
                            className="tw:size-full tw:object-cover"
                          />
                        ) : (
                          <Glyph size={14} />
                        )}
                      </span>
                    ))}
                  </div>
                  <span className="tw:text-[13px] tw:text-muted-foreground">
                    {group.container_types.length > 0
                      ? i18n.t('EditContainerTypeGroups:typesCount', {
                          count: group.container_types.length,
                        })
                      : i18n.t('EditContainerTypeGroups:noneTypes')}
                  </span>
                </div>

                {/* Linked stores */}
                <div className="tw:flex tw:items-center tw:gap-2">
                  <span
                    className="tw:size-[7px] tw:flex-none tw:rounded-full"
                    style={{
                      backgroundColor: group.has_linked_stores
                        ? BRAND_GREEN
                        : 'var(--border)',
                    }}
                  />
                  <span
                    className={
                      'tw:text-[13px] ' +
                      (group.has_linked_stores
                        ? 'tw:font-medium tw:text-foreground'
                        : 'tw:text-muted-foreground')
                    }
                  >
                    {group.has_linked_stores
                      ? i18n.t('Common:yes')
                      : i18n.t('Common:no')}
                  </span>
                </div>

                {/* Row action */}
                <div className="tw:flex tw:justify-end">
                  <button
                    type="button"
                    title={delTitle}
                    disabled={guarded || admin.deleting}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowDelete(group);
                    }}
                    className={
                      'tw:flex tw:size-[30px] tw:items-center tw:justify-center tw:rounded-md tw:transition-colors ' +
                      (guarded
                        ? 'tw:cursor-not-allowed tw:text-muted-foreground tw:opacity-35'
                        : 'tw:cursor-pointer tw:text-destructive tw:hover:bg-accent')
                    }
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* 5. Edit sheet */}
      {editorPanel}
    </div>
  );
}
